import { useSnackbar } from 'notistack'
import React, { useEffect, useMemo, useState } from 'react'
import ApiService from '@/services/Api'
import {
    CodecRoleType,
    ProductType,
    IntranetRoleType
} from '@/pages/Intranet/Companies'
import { CloseOutlined as CloseIcon } from '@mui/icons-material'
import {
    Grid,
    Button,
    Dialog,
    DialogActions,
    DialogTitle,
    CircularProgress,
    MenuItem,
    FormControl,
    InputLabel,
    FormControlLabel,
    Checkbox,
    Divider,
    Typography,
    TextField,
    Chip,
    Select,
    Box,
    IconButton
} from '@mui/material'
import ReactSelect from 'react-select'
import { maskPhone, unMask } from '@/components/Mask'
import useStyles from '../../pages/Intranet/Users/style'

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Grid item xs={12}>
        <Box
            display="flex"
            alignItems="center"
            sx={{
                mt: 2,
                mb: 1,
                gap: 1.5
            }}
        >
            <Typography
                variant="caption"
                sx={{
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                    whiteSpace: 'nowrap'
                }}
            >
                {children}
            </Typography>
            <Box sx={{ flex: 1, height: '1px', backgroundColor: '#e0e0e0' }} />
        </Box>
    </Grid>
)

export interface IUser {
    uuid?: string;
    companyUuid: string;
    name: string;
    email: string;
    product: ProductType
    role: CodecRoleType | IntranetRoleType
    pictureKey: string | null;
    preferences: any;
    phoneNumber: string | null;
    isReportsEnabled: boolean;
    isBetaTester: boolean;
    isAccountManager: boolean;
    createdAt: string;
    lastHeardAt: string | null;
    updatedAt: string;
    deletedAt?: string
    companyUuids: string[];
    permissions?: IUserPermissions;
}

export interface IUserPermissions {
    audience: Record<PermissionType, boolean>;
    campaigns: Record<PermissionType, boolean>;
    inventory: Record<PermissionType, boolean>;
    programs: Record<PermissionType, boolean>;
    whatsapp: Record<PermissionType, boolean>;
    studio: Record<PermissionType, boolean>;
    dial_campaigns: Record<PermissionType, boolean>;
    offline_campaigns: Record<PermissionType, boolean>;
}

export interface IAddress {
    uuid: string;
    country: string;
    state: string;
    city: string;
    neighborhood: string | null;
    street: string | null;
    number: string | null;
    complement: string | null;
    latitude: number | null;
    longitude: number | null;
}

export type RadioStatus = 'customer' | 'internal' | 'trial' | 'concurrence'

export interface IRadio {
    uuid: string;
    name: string;
    status?: RadioStatus;
    address: IAddress;
}

export type PermissionFamily =
    | 'audience'
    | 'campaigns'
    | 'inventory'
    | 'programs'
    | 'whatsapp'
    | 'studio'
    | 'dial_campaigns'
    | 'offline_campaigns'

export type PermissionType = 'read' | 'write' | 'delete' | 'update'

export const radioStatusLabels: Record<RadioStatus, string> = {
    customer: 'Cliente',
    internal: 'Interno',
    trial: 'Trial',
    concurrence: 'Concorrência'
}

export const radioStatusColors: Record<RadioStatus, string> = {
    customer: '#4caf50',
    internal: '#1976d2',
    trial: '#ff9800',
    concurrence: '#f44336'
}

// Status exibidos por padrão no select de rádios; 'concurrence' fica oculto até o usuário optar por vê-lo
export const defaultVisibleRadioStatuses: RadioStatus[] = ['customer', 'internal', 'trial']

export const exemptEmails = [
    'suporteloopert@loopert.com',
    'suporte2@loopert.com',
    'lucascarezia@gmail.com',
    'aline.oliveira@loopert.com',
    'rafael.motta@loopert.com',
    'gabriela@loopert.com',
    'wesley.reis@loopert.com',
    'joaokonflanz@gmail.com',
    'carloseven77@gmail.com',
    'christian.georges@loopert.com',
    'equipe@adrmedia.company'
]

export const isExemptEmail = (email?: string | null): boolean => {
    if (!email) return false
    const normalized = email.toLowerCase().trim()
    return normalized.endsWith('@loopert.com') || normalized.endsWith('@loopert') || exemptEmails.includes(email)
}

export const defaultPermissions: IUserPermissions = {
    audience: { read: false, write: false, delete: false, update: false },
    campaigns: { read: false, write: false, delete: false, update: false },
    inventory: { read: false, write: false, delete: false, update: false },
    programs: { read: false, write: false, delete: false, update: false },
    whatsapp: { read: false, write: false, delete: false, update: false },
    studio: { read: false, write: false, delete: false, update: false },
    dial_campaigns: { read: false, write: false, delete: false, update: false },
    offline_campaigns: { read: false, write: false, delete: false, update: false }
}

interface UserFormDialogProps {
    open: boolean
    onClose: () => void
    /** Pass a user object to edit it, or null/undefined to create a new user */
    userToEdit?: IUser | null
    radios: IRadio[]
    /** Lista completa de usuários, usada para validar regras como "um administrador por rádio" */
    users: IUser[]
    /** Pre-selects a company/radio when creating a new user (e.g. coming from a company page) */
    initialCompanyUuid?: string
    /**
     * Called after a successful save.
     * `createdPassword` is only present when a brand new user was created,
     * so the parent can show the "new password" modal.
     */
    onSaved: (createdPassword?: string) => void
}

const permissionFamilies: PermissionFamily[] = [
    'audience',
    'campaigns',
    'inventory',
    'programs',
    'whatsapp',
    'studio',
    'dial_campaigns',
    'offline_campaigns'
]

const permissionTypes: PermissionType[] = ['read', 'write', 'delete', 'update']

const familyLabels: Record<PermissionFamily, string> = {
    audience: 'Audiência',
    campaigns: 'Campanhas',
    inventory: 'Inventário',
    programs: 'Programas',
    whatsapp: 'WhatsApp',
    studio: 'Studio',
    dial_campaigns: 'Comprova Play',
    offline_campaigns: 'Campanhas Offline'
}

const permissionLabels: Record<PermissionType, string> = {
    read: 'Ler',
    update: 'Editar',
    write: 'Criar',
    delete: 'Apagar'
}

interface ValidationErrors {
    name?: string
    email?: string
    product?: string
    companyUuids?: string
}

// Quantidade máxima de opções renderizadas de uma vez no select de rádios.
// Sem esse limite, com muitos rádios (principalmente ao incluir "Concorrência")
// o react-select tenta renderizar todas as opções no DOM de uma vez e trava.
const MAX_VISIBLE_RADIO_OPTIONS = 100

// Definidos fora do componente para não serem recriados a cada render
// (o react-select re-renderiza a lista inteira quando esses objetos mudam de referência)
const reactSelectStyles = {
    menuPortal: (baseStyles: any) => ({ ...baseStyles, zIndex: 9999 }),
    control: (baseStyles: any) => ({
        ...baseStyles,
        minHeight: '40px',
        borderRadius: '4px',
        border: '1px solid rgba(0, 0, 0, 0.23)',
        boxShadow: 'none',
        fontFamily: 'Roboto, sans-serif',
        '&:hover': { border: '1px solid rgba(0, 0, 0, 0.87)' }
    }),
    menu: (baseStyles: any) => ({ ...baseStyles, zIndex: 9999, fontFamily: 'Roboto, sans-serif' }),
    multiValue: (baseStyles: any) => ({
        ...baseStyles,
        margin: '2px',
        fontFamily: 'Roboto, sans-serif',
        flexShrink: 0
    }),
    valueContainer: (baseStyles: any) => ({
        ...baseStyles,
        padding: '2px 8px',
        overflow: 'auto',
        whiteSpace: 'nowrap',
        flexWrap: 'nowrap',
        fontFamily: 'Roboto, sans-serif',
        maxWidth: 'none',
        width: 'auto',
        flexGrow: 1,
        display: 'flex',
        '::-webkit-scrollbar': { height: '4px' },
        '::-webkit-scrollbar-thumb': {
            background: 'rgba(0,0,0,0.2)',
            borderRadius: '2px'
        }
    }),
    container: (baseStyles: any) => ({ ...baseStyles, width: '100%', minWidth: '250px' }),
    option: (baseStyles: any, { isSelected }: any) => ({
        ...baseStyles,
        fontFamily: 'Roboto, sans-serif',
        backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.08)' : baseStyles.backgroundColor,
        color: isSelected ? '#1976d2' : baseStyles.color,
        '&:active': {
            backgroundColor: isSelected ? 'rgba(11, 83, 156, 0.56)' : 'rgba(228, 230, 233, 0.16)'
        }
    }),
    input: (baseStyles: any) => ({ ...baseStyles, fontFamily: 'Roboto, sans-serif', flexShrink: 0 }),
    placeholder: (baseStyles: any) => ({ ...baseStyles, fontFamily: 'Roboto, sans-serif' }),
    singleValue: (baseStyles: any) => ({ ...baseStyles, fontFamily: 'Roboto, sans-serif' })
}

const RadioMultiValueContainer = ({ children, ...props }: any) => (
    <div style={{ display: 'flex', flexShrink: 0 }} {...props}>
        {children}
    </div>
)

const radioFormatOptionLabel = (option: { value: string; label: string; status?: RadioStatus }) => (
    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {option.status && (
            <span
                style={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    backgroundColor: radioStatusColors[option.status],
                    flexShrink: 0,
                    display: 'inline-block'
                }}
            />
        )}
        {option.label}
    </span>
)

const emptyUser = (): IUser => ({
    uuid: '',
    companyUuid: '',
    name: '',
    email: '',
    product: 'metrics',
    role: 'admin',
    phoneNumber: null,
    isReportsEnabled: false,
    pictureKey: null,
    preferences: null,
    isBetaTester: false,
    isAccountManager: false,
    createdAt: '',
    lastHeardAt: null,
    updatedAt: '',
    companyUuids: [],
    permissions: defaultPermissions
})

const UserFormDialog: React.FC<UserFormDialogProps> = ({
    open,
    onClose,
    userToEdit,
    radios,
    initialCompanyUuid,
    onSaved
}) => {
    const classes = useStyles()
    const { enqueueSnackbar } = useSnackbar()

    const [user, setUser] = useState<IUser | null>(null)
    const [companies, setCompanies] = useState<string[]>([])
    const [userPermissions, setUserPermissions] = useState<IUserPermissions>(defaultPermissions)
    const [visibleRadioStatuses, setVisibleRadioStatuses] = useState<RadioStatus[]>(defaultVisibleRadioStatuses)
    const [isSaving, setIsSaving] = useState(false)
    // Valor exibido no input imediatamente (sem lag ao digitar)
    const [radioSearchInput, setRadioSearchInput] = useState('')
    // Valor usado de fato pra filtrar a lista, atualizado com um pequeno atraso
    // pra não recalcular/re-renderizar a lista gigante a cada tecla digitada
    const [debouncedRadioSearch, setDebouncedRadioSearch] = useState('')

    useEffect(() => {
        const timeout = setTimeout(() => setDebouncedRadioSearch(radioSearchInput), 150)
        return () => clearTimeout(timeout)
    }, [radioSearchInput])

    // (Re)initializes the form whenever the dialog is opened
    useEffect(() => {
        if (!open) return

        if (userToEdit) {
            const initialUser: IUser = {
                ...userToEdit,
                role: 'admin'
            }

            const mergedPermissions: IUserPermissions = {
                ...defaultPermissions,
                ...(userToEdit.permissions || {}),
                offline_campaigns:
                    userToEdit.permissions?.offline_campaigns || defaultPermissions.offline_campaigns
            }

            setUser(initialUser)
            setUserPermissions(mergedPermissions)
            setCompanies(
                initialUser.companyUuids.length > 0
                    ? initialUser.companyUuids
                    : initialUser.companyUuid
                        ? [initialUser.companyUuid]
                        : []
            )
        } else {
            const newUser = emptyUser()
            setUser(newUser)
            setUserPermissions(defaultPermissions)
            setCompanies(initialCompanyUuid ? [initialCompanyUuid] : [])
        }

        setVisibleRadioStatuses(defaultVisibleRadioStatuses)
        setRadioSearchInput('')
        setDebouncedRadioSearch('')
    }, [open, userToEdit, initialCompanyUuid])

    const toggleRadioStatusVisibility = (status: RadioStatus) => {
        setVisibleRadioStatuses(prev =>
            prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
        )
    }

    // Todas as opções de rádio, num formato estável — só recalcula se a lista de rádios mudar
    const allRadioOptions = useMemo(
        () =>
            radios.map(radio => ({
                value: radio.uuid,
                label: `${radio.name} - ${radio.address.city}`,
                status: radio.status
            })),
        [radios]
    )

    // Mapa uuid -> opção, pra montar a lista de selecionados em O(1) por item
    // em vez de varrer a lista inteira de rádios de novo
    const radioOptionsByUuid = useMemo(() => {
        const map = new Map<string, { value: string; label: string; status?: RadioStatus }>()
        allRadioOptions.forEach(option => map.set(option.value, option))
        return map
    }, [allRadioOptions])

    // Rádios com status fora de `visibleRadioStatuses` não aparecem como opção
    // (rádios sem status definido são tratados como 'customer' por padrão)
    const radioOptionsForVisibleStatuses = useMemo(
        () => allRadioOptions.filter(option => visibleRadioStatuses.includes(option.status || 'customer')),
        [allRadioOptions, visibleRadioStatuses]
    )

    // Lista efetivamente renderizada no dropdown: filtrada pela busca (com debounce) e
    // limitada a MAX_VISIBLE_RADIO_OPTIONS itens, sempre priorizando os já selecionados
    // pra que continuem aparecendo marcados na lista
    const visibleRadioOptions = useMemo(() => {
        const search = debouncedRadioSearch.trim().toLowerCase()
        const matchesSearch = (option: { label: string }) =>
            !search || option.label.toLowerCase().includes(search)

        const eligible = radioOptionsForVisibleStatuses.filter(matchesSearch)
        const selectedSet = new Set(companies)
        const selected = eligible.filter(option => selectedSet.has(option.value))
        const unselected = eligible.filter(option => !selectedSet.has(option.value))

        return {
            options: [...selected, ...unselected].slice(0, MAX_VISIBLE_RADIO_OPTIONS),
            totalMatches: eligible.length
        }
    }, [radioOptionsForVisibleStatuses, debouncedRadioSearch, companies])

    // Usa o mapa completo de rádios (não a lista filtrada/limitada) para que uma rádio já
    // selecionada continue aparecendo como chip mesmo se o filtro de status/busca escondê-la
    const selectedRadioOptions = useMemo(
        () => companies.map(uuid => radioOptionsByUuid.get(uuid)).filter(Boolean) as { value: string; label: string; status?: RadioStatus }[],
        [companies, radioOptionsByUuid]
    )

    const handleChange = (selectedOptions: any) => {
        if (!selectedOptions) {
            setCompanies([])
            return
        }

        if (Array.isArray(selectedOptions)) {
            const isExemptUser = isExemptEmail(user?.email)

            if (!isExemptUser && selectedOptions.length > 75) {
                enqueueSnackbar('Não é possível selecionar mais de 25 rádios.', { variant: 'warning' })
                return
            }
            setCompanies(selectedOptions.map((option: any) => option.value))
        } else {
            setCompanies([selectedOptions.value])
        }
    }

    const selectProduct = ({ target }: { target: { name?: string; value: unknown } }) => {
        setUser(prevUser => {
            if (!prevUser) return null

            const isBetaTesterValue = target.value === 'metrics' ? prevUser.isBetaTester : false

            return {
                ...prevUser,
                product: target.value as IUser['product'],
                isBetaTester: isBetaTesterValue
            }
        })
    }

    const validateUser = (candidate: IUser): ValidationErrors => {
        const errors: ValidationErrors = {}

        if (!candidate?.name) {
            errors.name = 'O campo nome é obrigatório.'
        }
        if (!candidate?.email) {
            errors.email = 'O campo email é obrigatório.'
        }
        if (!candidate?.product) {
            errors.product = 'O campo produto é obrigatório.'
        }
        if (candidate.product !== 'intranet' && (!candidate?.companyUuids || candidate.companyUuids.length === 0)) {
            errors.companyUuids = 'Pelo menos uma companhia deve ser selecionada.'
        }

        const isExemptUser = isExemptEmail(candidate?.email)
        if (!isExemptUser && candidate?.companyUuids && candidate.companyUuids.length > 75) {
            errors.companyUuids = 'Não é possível selecionar mais de 25 rádios.'
        }

        return errors
    }

    const handleSave = async () => {
        if (!user) return

        const newUser: IUser = {
            uuid: user.uuid || undefined,
            companyUuid: companies[0],
            companyUuids: companies,
            name: user.name || '',
            email: user.email || '',
            product: user.product || 'metrics',
            role: 'admin',
            isBetaTester: user.isBetaTester || false,
            phoneNumber: user.phoneNumber || null,
            isReportsEnabled: user.isReportsEnabled || false,
            isAccountManager: user.isAccountManager || false,
            pictureKey: user.pictureKey || null,
            preferences: user.preferences || null,
            createdAt: user.createdAt || '',
            lastHeardAt: user.lastHeardAt || null,
            updatedAt: user.updatedAt || '',
            permissions: userPermissions
        }

        const errors = validateUser(newUser)
        if (Object.keys(errors).length > 0) {
            Object.values(errors).forEach(error => {
                enqueueSnackbar(error, { variant: 'error' })
            })
            return
        }

        const isNewUser = !user.uuid

        setIsSaving(true)
        try {
            const response = await ApiService.post('/user', newUser)
            if (response.data) {
                enqueueSnackbar('Usuário salvo com sucesso.', { variant: 'success' })
                onSaved(isNewUser ? response.data.password : undefined)
                onClose()
            }
        } catch (error: any) {
            console.error('Erro ao salvar usuário:', error)
            enqueueSnackbar(`${error.data}`, { variant: 'error' })
        } finally {
            setIsSaving(false)
        }
    }

    const renderPermissionsSection = () => (
        <Grid item xs={12}>
            <Box display="flex" flexDirection="column" gap={1}>
                {permissionFamilies.map(family => {
                    const allChecked = Object.values(userPermissions[family]).every(v => v)
                    const isDisabledFamily = false

                    return (
                        <Box
                            key={family}
                            sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: '8px',
                                p: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: 1.5,
                                transition: 'border-color 0.2s, background-color 0.2s',
                                opacity: isDisabledFamily ? 0.5 : 1,
                                backgroundColor: allChecked && !isDisabledFamily ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
                                '&:hover': {
                                    borderColor: isDisabledFamily ? 'divider' : 'primary.main'
                                }
                            }}
                        >
                            <Box sx={{ minWidth: 140, flexShrink: 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {familyLabels[family]}
                                </Typography>
                            </Box>

                            <Button
                                id={`${family}_all_permissions_button`}
                                size="small"
                                sx={{ minWidth: 0, px: 1.5, py: 0.25, fontSize: 11 }}
                                variant={allChecked ? 'contained' : 'outlined'}
                                color="primary"
                                disabled={isDisabledFamily}
                                onClick={() => {
                                    setUserPermissions(prev => ({
                                        ...prev,
                                        [family]: {
                                            read: !allChecked,
                                            write: !allChecked,
                                            delete: !allChecked,
                                            update: !allChecked
                                        }
                                    }))
                                }}
                            >
                                Todos
                            </Button>

                            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {permissionTypes.map(type => (
                                    <FormControlLabel
                                        key={`${family}-${type}`}
                                        sx={{ mr: 1.5 }}
                                        disabled={isDisabledFamily}
                                        control={
                                            <Checkbox
                                                id={`${family}_${type}_permission_checkbox`}
                                                size="small"
                                                checked={userPermissions[family][type]}
                                                disabled={isDisabledFamily}
                                                onChange={e => {
                                                    setUserPermissions(prev => ({
                                                        ...prev,
                                                        [family]: {
                                                            ...prev[family],
                                                            [type]: e.target.checked
                                                        }
                                                    }))
                                                }}
                                            />
                                        }
                                        label={<Typography variant="body2">{permissionLabels[type]}</Typography>}
                                    />
                                ))}
                            </Box>
                        </Box>
                    )
                })}
            </Box>
        </Grid>
    )

    return (
        <Dialog
            open={open}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { borderRadius: '12px' }
            }}
        >
            <DialogTitle sx={{ pr: 6, position: 'relative' }}>
                {!user?.uuid ? 'Criando novo ' : 'Editando o '}usuário
                <IconButton
                    aria-label="Fechar"
                    onClick={onClose}
                    disabled={isSaving}
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        color: 'text.secondary'
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            {!user && (
                <Grid container justifyContent="center" sx={{ py: 6 }}>
                    <CircularProgress size={80} color="primary" />
                </Grid>
            )}

            {user && (
                <>
                    <Grid container spacing={2} sx={{ px: 3 }}>
                        <SectionTitle>Dados Básicos</SectionTitle>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                id="user_name_field"
                                fullWidth
                                label="Nome"
                                size="small"
                                variant="outlined"
                                value={user.name}
                                onChange={e => setUser({ ...user, name: e.target.value })}
                                inputProps={{
                                    'data-lpignore': 'true',
                                    'data-1password': 'true',
                                    'data-op-ignore': 'true'
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                id="user_email_field"
                                fullWidth
                                label="Email"
                                size="small"
                                variant="outlined"
                                value={user.email}
                                onChange={e => setUser({ ...user, email: e.target.value })}
                                inputProps={{
                                    'data-lpignore': 'true',
                                    'data-1password': 'true',
                                    'data-op-ignore': 'true'
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth variant="outlined" size="small">
                                <InputLabel id="product_select_label">Produto</InputLabel>
                                <Select
                                    id="product_select_field"
                                    value={user.product}
                                    onChange={selectProduct}
                                    variant="outlined"
                                    color="primary"
                                    label="Produto"
                                    className={classes.select}
                                    labelId="product_select_label"
                                >
                                    <MenuItem value="metrics">Metrics</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                id="user_phone_field"
                                fullWidth
                                className={classes.itemInput}
                                label="Número de telefone (WhatsApp)"
                                size="small"
                                color="primary"
                                variant="outlined"
                                value={maskPhone(user.phoneNumber || undefined)}
                                onChange={({ target }) => setUser({ ...user, phoneNumber: unMask(target.value) })}
                                inputProps={{
                                    'data-lpignore': 'true',
                                    'data-1password': 'true',
                                    'data-op-ignore': 'true'
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        id="is_reports_enabled_checkbox"
                                        checked={user.isReportsEnabled}
                                        onChange={e => setUser({ ...user, isReportsEnabled: e.target.checked })}
                                    />
                                }
                                label="Envio automático de relatórios"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        id="beta_tester_checkbox"
                                        checked={user.isBetaTester}
                                        onChange={e => setUser({ ...user, isBetaTester: e.target.checked })}
                                    />
                                }
                                label="Acesso a recursos em beta"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        id="is_account_manager_checkbox"
                                        checked={user.isAccountManager}
                                        onChange={e => setUser({ ...user, isAccountManager: e.target.checked })}
                                    />
                                }
                                label="Gerente de conta"
                            />
                        </Grid>

                        <SectionTitle>Rádios</SectionTitle>

                        <Grid item xs={12}>
                            <FormControl fullWidth variant="outlined" size="small">
                                <div style={{ position: 'relative' }}>
                                    <label
                                        style={{
                                            position: 'absolute',
                                            backgroundColor: 'white',
                                            padding: '0 4px',
                                            top: '-10px',
                                            left: '10px',
                                            fontSize: '12px',
                                            zIndex: 1,
                                            color: 'rgba(0, 0, 0, 0.6)',
                                            fontFamily: 'Roboto, sans-serif'
                                        }}
                                    >
                                        Rádio
                                    </label>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', margin: '4px 0 8px' }}>
                                        {(['customer', 'internal', 'trial', 'concurrence'] as RadioStatus[]).map(status => {
                                            const isVisible = visibleRadioStatuses.includes(status)
                                            return (
                                                <Chip
                                                    key={status}
                                                    id={`radio_status_filter_${status}_chip`}
                                                    size="small"
                                                    clickable
                                                    onClick={() => toggleRadioStatusVisibility(status)}
                                                    variant={isVisible ? 'filled' : 'outlined'}
                                                    style={{
                                                        opacity: isVisible ? 1 : 0.55,
                                                        fontFamily: 'Roboto, sans-serif'
                                                    }}
                                                    label={
                                                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                            <span
                                                                style={{
                                                                    width: 8,
                                                                    height: 8,
                                                                    borderRadius: '50%',
                                                                    backgroundColor: radioStatusColors[status],
                                                                    display: 'inline-block',
                                                                    flexShrink: 0
                                                                }}
                                                            />
                                                            {radioStatusLabels[status]}
                                                        </span>
                                                    }
                                                />
                                            )
                                        })}
                                    </div>
                                    <ReactSelect
                                        inputId="radio_select_field"
                                        isMulti
                                        value={selectedRadioOptions}
                                        onChange={handleChange}
                                        options={visibleRadioOptions.options}
                                        placeholder="Selecione as rádios..."
                                        classNamePrefix="react-select"
                                        isSearchable
                                        hideSelectedOptions={false}
                                        isClearable={false}
                                        closeMenuOnSelect={false}
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                        // Já filtramos/limitamos as opções manualmente (com debounce),
                                        // então desligamos o filtro/matching interno do react-select
                                        filterOption={() => true}
                                        inputValue={radioSearchInput}
                                        onInputChange={(newValue, actionMeta) => {
                                            if (actionMeta.action === 'input-change') {
                                                setRadioSearchInput(newValue)
                                            }
                                        }}
                                        formatOptionLabel={radioFormatOptionLabel}
                                        isOptionDisabled={() => {
                                            const isExemptUser = isExemptEmail(user?.email)
                                            return !isExemptUser && companies.length >= 75
                                        }}
                                        noOptionsMessage={() =>
                                            debouncedRadioSearch
                                                ? 'Nenhuma rádio encontrada'
                                                : 'Nenhuma rádio disponível'
                                        }
                                        styles={reactSelectStyles}
                                        components={{
                                            MultiValueContainer: RadioMultiValueContainer
                                        }}
                                    />
                                    {visibleRadioOptions.totalMatches > MAX_VISIBLE_RADIO_OPTIONS && (
                                        <Typography
                                            variant="caption"
                                            sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}
                                        >
                                            Mostrando {MAX_VISIBLE_RADIO_OPTIONS} de {visibleRadioOptions.totalMatches} rádios. Refine a busca para ver mais.
                                        </Typography>
                                    )}
                                </div>
                            </FormControl>
                        </Grid>

                        <SectionTitle>Permissões</SectionTitle>

                        {renderPermissionsSection()}
                    </Grid>

                    <Divider sx={{ mt: 2 }} />

                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button onClick={onClose} color="primary" disabled={isSaving}>
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleSave}
                            color="primary"
                            variant="contained"
                            disabled={isSaving}
                            endIcon={isSaving && <CircularProgress size={15} color="inherit" />}
                        >
                            {user.uuid ? 'Salvar' : 'Criar'}
                        </Button>
                    </DialogActions>
                </>
            )}
        </Dialog>
    )
}

export default UserFormDialog
